import {Button, Form, FormInstance, Input, message, Select, Tabs} from 'antd';
import {t} from 'i18next';
import {FC, ReactNode, useState} from 'react';
import cn from 'classnames';
import {EnFlag, SaFlag} from '../Icons';
import {langPlaceHolderContent, RULES} from '@/constants';
import InfoTooltip from '../info-tooltip';
import styles from './index.module.scss';
import {useTranslateField} from '@/hooks';
import {Translation} from '@/models';

export interface IChangedLangFormFieldsProps {
  language: 'ar' | 'en';
  form: FormInstance<any>;
}

export interface IFormFieldWithTranslationProps {
  language: 'ar' | 'en';
  children: ReactNode | ReactNode[];
  name: string;
  form: FormInstance<any>;
}

const FormFieldWithTranslation: FC<IFormFieldWithTranslationProps> = ({
                                                                        language,
                                                                        children,
                                                                        name,
                                                                        form,
                                                                      }) => {
  const {isLoading, translateField} = useTranslateField();

  const handleFieldClick = (fieldName: string) => {
    const arabicFieldVal = form.getFieldValue(fieldName);
    if (!arabicFieldVal) {
      message.error(t('pleaseFillTheArabicField'));
    } else {
      translateField(form.getFieldValue(fieldName), (data: Translation) => {
        const fieldEnglishName = `${name}En`;
        form.setFieldsValue({
          [fieldEnglishName]:
            fieldEnglishName === 'toolsEn'
              ? data?.translatedText.split(' ')
              : data?.translatedText,
        });
      });
    }
  };

  return (
    <>
      {children}
      {language === 'en' && (
        <Button
          className={styles.translateBtn}
          type='primary'
          loading={isLoading}
          onClick={() => handleFieldClick(name)}
        >
          {t('translate')}
        </Button>
      )}
    </>
  );
};
const ChangedLangFormFields = ({
                                 language,
                                 form,
                               }: IChangedLangFormFieldsProps) => {
  const langRule = language === 'ar' ? RULES.arabicField : RULES.englishField;
  const languageFieldName = language === 'ar' ? '' : 'En';
  const isOnline = Form.useWatch('isOnline', form);
  return (
    <>
      {/* experience name  */}
      <FormFieldWithTranslation form={form} name='name' language={language}>
        <Form.Item
          rules={[RULES.required, RULES.minLengthValidator(7), langRule]}
          name={`name${languageFieldName}`}
          label={
            <>
              <span>{`${t('expName')} ${t(language)}`}:</span>
              <InfoTooltip
                title={`${t('minLengthValidationError', {
                  minLength: 7,
                })}`}
              />
            </>
          }
        >
          <Input type='text'/>
        </Form.Item>
      </FormFieldWithTranslation>

      {/* About experience */}
      <FormFieldWithTranslation
        form={form}
        name='description'
        language={language}
      >
        <Form.Item
          name={`description${languageFieldName}`}
          label={
            <>
              <span>{`${t('aboutExp')} ${t(language)}`}:</span>
              <InfoTooltip
                title={`${t('minLengthValidationError', {
                  minLength: 30,
                })}`}
              />
            </>
          }
          rules={[RULES.required, RULES.minLengthValidator(30), langRule]}
        >
          <Input.TextArea rows={3} autoSize={{minRows: 3, maxRows: 6}}/>
        </Form.Item>
      </FormFieldWithTranslation>

      {/* required tools */}
      <FormFieldWithTranslation form={form} name='tools' language={language}>
        <Form.Item
          name={`tools${languageFieldName}`}
          label={`${t('requiredTools')} ${t(language)}:`}
        >
          <Select
            mode='tags'
            placeholder={t('requiredToolsLabel')}
            getPopupContainer={trigger => trigger.parentNode}
          />
        </Form.Item>
      </FormFieldWithTranslation>

      {/* location details  */}
      <FormFieldWithTranslation
        form={form}
        name='locationDetails'
        language={language}
      >
        <Form.Item
          name={`locationDetails${languageFieldName}`}
          label={`${t('locationDetails')} ${t(language)}:`}
          rules={isOnline ? [langRule] : [RULES.required, langRule]}
        >
          <Input.TextArea
            rows={3}
            autoSize={{minRows: 3, maxRows: 6}}
            placeholder={langPlaceHolderContent.locationDetails[language]}
          />
        </Form.Item>
      </FormFieldWithTranslation>

      {/* Welcoming Message */}
      <FormFieldWithTranslation
        form={form}
        name='welcomeMessage'
        language={language}
      >
        <Form.Item
          name={`welcomeMessage${languageFieldName}`}
          label={`${t('welcomingMessage')} ${t(language)}:`}
          rules={[
            RULES.required,
            RULES.minLengthValidator(30),
            RULES.maxLengthValidator(140),
            langRule,
          ]}
        >
          <Input.TextArea
            rows={3}
            autoSize={{minRows: 3, maxRows: 6}}
            placeholder={langPlaceHolderContent.welcomingMessage[language]}
          />
        </Form.Item>
      </FormFieldWithTranslation>
    </>
  );
};
const ChangedLangTabs = ({form}: { form: FormInstance<any> }) => {
  const [selectedLang, setSelectedLang] = useState<'ar' | 'en'>('ar');
  return (
    <Tabs
      centered
      defaultActiveKey='ar'
      size='middle'
      onChange={(key: string) => setSelectedLang(key as 'ar' | 'en')}
      className={cn(styles.languageTabs, {
        [styles.ar]: selectedLang === 'ar',
        [styles.en]: selectedLang === 'en',
      })}
      items={[
        {
          forceRender: true,

          key: 'ar',
          label: (
            <span className={styles.languageTab}>
              <SaFlag/>
              {t('arabic')}
            </span>
          ),
          children: <ChangedLangFormFields form={form} language='ar'/>,
        },
        {
          key: 'en',
          forceRender: true,
          label: (
            <span className={styles.languageTab}>
              <EnFlag/>
              {t('english')}
            </span>
          ),
          children: <ChangedLangFormFields form={form} language='en'/>,
        },
      ]}
    />
  );
};

export default ChangedLangTabs;
